import { useState, useEffect, useCallback } from 'react';
import {
  subscribeToPendingApprovals,
  approveReference,
  rejectReference,
  updatePendingApproval,
  mergeIntoExistingReference,
  dismissApprovalAsDuplicate,
} from '../services/firebase';
import type { PendingReferenceApproval, ApprovalStatus, MergeOptions } from '../types';

interface UsePendingApprovalsOptions {
  status?: ApprovalStatus;
}

export function usePendingApprovals(options: UsePendingApprovalsOptions = {}) {
  const [approvals, setApprovals] = useState<PendingReferenceApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToPendingApprovals((data) => {
      setApprovals(data);
      setLoading(false);
    }, options.status);

    return unsubscribe;
  }, [options.status]);

  const approve = useCallback(async (id: string) => {
    try {
      await approveReference(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
      throw err;
    }
  }, []);

  const reject = useCallback(async (id: string, reason?: string) => {
    try {
      await rejectReference(id, reason);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
      throw err;
    }
  }, []);

  const update = useCallback(
    async (id: string, updates: Partial<PendingReferenceApproval>) => {
      try {
        await updatePendingApproval(id, updates);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update');
        throw err;
      }
    },
    []
  );

  const merge = useCallback(async (pendingId: string, options: MergeOptions) => {
    try {
      await mergeIntoExistingReference(pendingId, options);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge');
      throw err;
    }
  }, []);

  const dismissDuplicate = useCallback(
    async (
      id: string,
      match: { brandId: string; referenceId: string },
      resolvedByUid?: string,
    ) => {
      try {
        await dismissApprovalAsDuplicate(id, match, resolvedByUid);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to dismiss');
        throw err;
      }
    },
    [],
  );

  return {
    approvals,
    loading,
    error,
    approve,
    reject,
    update,
    merge,
    dismissDuplicate,
    pendingCount: approvals.filter((a) => a.status === 'pending').length,
  };
}
